/**
 * Marker Clustering
 * Groups nearby markers when zoomed out for better performance and clarity
 */

import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

/**
 * Create a marker cluster group with custom options
 * @param {Object} options - Clustering options
 * @returns {L.MarkerClusterGroup} Cluster group
 */
export function createClusterGroup(options = {}) {
    const defaultOptions = {
        // Show clusters when there are 2+ markers in close proximity
        maxClusterRadius: 60,

        // Animate adding markers
        animateAddingMarkers: true,

        // Don't show coverage polygon on hover
        showCoverageOnHover: false,

        // Zoom to bounds when clicking cluster
        zoomToBoundsOnClick: true,

        // Spiderfy markers at max zoom instead of clustering
        spiderfyOnMaxZoom: true,

        // Remove cluster outlines
        removeOutsideVisibleBounds: true,

        // Custom cluster icon creator
        iconCreateFunction: function(cluster) {
            const count = cluster.getChildCount();
            let sizeClass = 'small';

            if (count >= 10) {
                sizeClass = 'large';
            } else if (count >= 5) {
                sizeClass = 'medium';
            }

            return L.divIcon({
                html: `<div class="cluster-inner"><span>${count}</span></div>`,
                className: `marker-cluster marker-cluster-${sizeClass}`,
                iconSize: L.point(40, 40)
            });
        }
    };

    return L.markerClusterGroup({ ...defaultOptions, ...options });
}

/**
 * Create category-based cluster groups
 * Each category has its own cluster group with custom styling
 * @param {Object} iconsByCategory - Icons keyed by category
 * @param {Function} createMarkerCallback - Function to create markers
 * @returns {Object} Cluster groups keyed by category
 */
export function createCategorizedClusters(iconsByCategory, createMarkerCallback) {
    const clusterGroups = {};

    Object.keys(iconsByCategory).forEach(category => {
        clusterGroups[category] = createClusterGroup({
            // Optional: different cluster settings per category
        });
    });

    return clusterGroups;
}

export default { createClusterGroup, createCategorizedClusters };
